import React from 'react';
import { tagMaterials } from '@/components/jewellery/JewelleryTagViewer';
import { formatCarats } from '@/lib/format';
import { priceTag, inr } from '@/lib/tagPricing';

const g3 = (n) => Number(n || 0).toFixed(3);
const fmtD = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const money = (n) => (n == null ? '—' : `₹${inr(n)}`);

export function usePublicPrice(data) {
  const p = data?.pricing;
  if (!p) return null;
  const rm = data.view?.rm_mode || 'collapsed';
  const materials = tagMaterials(data, rm);
  return { ...priceTag(data, rm, p.card, materials), materials, gold: p.gold, mode: p.mode };
}

export function GoldRateNote({ gold }) {
  return (
    <div className="mt-2 text-center text-[11px] text-slate-500" data-testid="public-tag-gold-rate">
      {gold ? <>Gold rate considered: <b className="text-slate-700">₹{inr(gold.rate)}/g</b> for 999 (100%) gold{gold.date ? ` · ${fmtD(gold.date)}` : ''}</> : 'Gold rate not available — gold value not included.'}
    </div>
  );
}

export function MissingPricesNote({ missing }) {
  return (
    <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-left text-sm font-bold text-red-600" data-testid="public-tag-missing-prices">
      Prices not configured for: {missing.join(', ')}
    </div>
  );
}

export function PublicTotalValue({ price }) {
  const miss = price.missing.length > 0;
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-center shadow-sm" data-testid="public-tag-total-block">
      {miss ? <MissingPricesNote missing={price.missing} /> : <>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total sale value</div>
        <div className="font-mono text-2xl font-bold text-slate-900" data-testid="public-tag-total-value">{money(price.total)}</div>
      </>}
      {price.gold ? <GoldRateNote gold={price.gold} /> : null}
    </div>
  );
}

// Single-column priced tag for phones: header, raw materials with amounts, labour, gold, total — no sideways scroll.
export function PublicPricedTag({ data, price }) {
  const showPcs = !!data.view?.show_pieces;
  return (
    <div className="mx-auto w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="public-priced-tag">
      <div className="flex items-start gap-3 p-4">
        {data.image ? <img src={data.image} alt="" className="h-24 w-24 shrink-0 rounded-xl border border-slate-100 object-cover" data-testid="public-priced-tag-image" /> : null}
        <div className="min-w-0 space-y-1">
          <div className="break-all font-mono text-base font-bold text-slate-900" data-testid="public-priced-tag-jno">{data.jewelleryNo}</div>
          <div className="text-sm text-slate-600">Purity <b className="text-slate-800">{data.purityText || '—'}</b></div>
          <div className="text-sm text-slate-600">Gross wt <b className="font-mono text-slate-800">{g3(data.gross)} g</b></div>
        </div>
      </div>
      <div className="border-t border-slate-200 px-4 pb-4 pt-3">
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Raw material</div>
        <div className="divide-y divide-slate-100">
          <div className="flex items-start justify-between gap-3 py-2" data-testid="public-priced-tag-gold">
            <div className="pl-1"><div className="text-sm font-semibold text-slate-800">Net weight</div><div className="font-mono text-[12px] text-slate-600">{g3(data.net)} g · Gold</div></div>
            <div className="shrink-0 font-mono text-sm font-semibold text-slate-900">{money(price.goldAmt)}</div>
          </div>
          {price.materials.map((m) => (
            <div key={m.key} className="flex items-start justify-between gap-3 py-2" data-testid="public-priced-tag-line">
              <div className="min-w-0 pl-1">
                <div className="break-words text-sm font-semibold text-slate-800">{m.classification}</div>
                {m.note ? <div className="break-words text-[11px] text-slate-500">{m.note}</div> : null}
                <div className="mt-0.5 font-mono text-[12px] text-slate-600">{formatCarats(m.carats)} ct{showPcs && !m.hidePieces && m.pieces ? ` · ${m.pieces} pcs` : ''}</div>
              </div>
              <div className={`shrink-0 font-mono text-sm font-semibold ${price.byKey[m.key]?.amount == null ? 'text-red-600' : 'text-slate-900'}`}>{money(price.byKey[m.key]?.amount)}</div>
            </div>
          ))}
          <div className="flex items-start justify-between gap-3 py-2" data-testid="public-priced-tag-labour">
            <div className="pl-1 text-sm font-semibold text-slate-800">Labour</div>
            <div className="shrink-0 font-mono text-sm font-semibold text-slate-900">{money(price.labourAmt)}</div>
          </div>
          {price.charges.map((c, i) => (
            <div key={i} className="flex items-start justify-between gap-3 py-2" data-testid="public-priced-tag-charge">
              <div className="pl-1 text-sm font-semibold text-slate-800">{c.name}</div>
              <div className={`shrink-0 font-mono text-sm font-semibold ${c.amount == null ? 'text-red-600' : 'text-slate-900'}`}>{money(c.amount)}</div>
            </div>
          ))}
        </div>
        {price.missing.length ? <MissingPricesNote missing={price.missing} /> : (
          <div className="mt-2 flex items-center justify-between rounded-xl bg-slate-900 px-3 py-2.5 text-white" data-testid="public-priced-tag-total">
            <span className="text-sm font-semibold">Total sale value</span>
            <span className="font-mono text-lg font-bold">{money(price.total)}</span>
          </div>
        )}
        {price.gold ? <GoldRateNote gold={price.gold} /> : null}
      </div>
    </div>
  );
}
